package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceProgressResponse
import ai.jessie.shared.invoices.InvoiceResponse
import ai.jessie.shared.invoices.InvoiceStateEventResponse
import ai.jessie.web.members.localTime
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertListStyle
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.LabelStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.ProgressStepsStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// UC-014 for one opened invoice. The server decides access and the permitted next states; showing a
// control grants nothing. State lives only as long as this invoice of this organisation is shown.
@Composable
internal fun invoiceProgression(
    organisationId: String,
    invoiceId: String,
    changed: (InvoiceResponse) -> Unit,
    accessLost: (OnboardingProblem) -> Unit,
) {
    val port = LocalInvoiceProgressPort.current
    val scope = rememberCoroutineScope()
    val model =
        remember(port, scope, organisationId, invoiceId) {
            InvoiceProgressState(port, scope, organisationId, invoiceId, changed, accessLost)
        }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    invoiceProgress(model.state, model.actions)
}

@Composable
internal fun invoiceProgress(state: InvoiceProgressUiState, actions: InvoiceProgressActions) {
    Section(attrs = CardStyle.toAttrs { attr("aria-labelledby", PROGRESS_TITLE_ID) }) {
        Div(attrs = CardHeadStyle.toAttrs()) {
            H2(
                attrs = {
                    id(PROGRESS_TITLE_ID)
                    attr("tabindex", "-1")
                }
            ) {
                Text("Status")
            }
        }
        Div(attrs = CardBodyStyle.toAttrs()) { progressBody(state, actions) }
    }
}

@Composable
private fun progressBody(state: InvoiceProgressUiState, actions: InvoiceProgressActions) {
    progressFocus(state)
    progressProblem(state.error)
    readiness(state.readiness)
    if (state.busy)
        P(attrs = StatusStyle.toAttrs { attr("role", "status") }) {
            Text("Status wird geladen oder erfasst …")
        }
    state.notice?.let { P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(it) } }
    val progress = state.progress
    if (progress == null) {
        if (state.error != null) reloadProgress(state.busy, actions)
        return
    }
    // Step 4.
    progressSteps(progress.invoice.status)
    P(attrs = SmallStyle.toAttrs()) {
        Text("Aktueller Status: ${invoiceStatusLabel(progress.invoice.status)}")
    }
    val target = state.target
    if (target == null) nextStates(progress, state.busy, actions)
    else transitionConfirmation(progress, target, state, actions)
    stateHistory(progress.history)
    reloadProgress(state.busy || target != null, actions)
}

@Composable
private fun reloadProgress(disabled: Boolean, actions: InvoiceProgressActions) {
    Div(attrs = RowStyle.toAttrs()) {
        themedButton(
            "Status erneut laden",
            actions.refresh,
            variant = ButtonVariant.GHOST,
            size = ButtonSize.SMALL,
            disabled = disabled,
        )
    }
}

// BR-081 orders Draft, Ready, Sent and Paid; a Cancelled invoice has left that path, so only its
// badge in the record and the history show where it stopped.
@Composable
private fun progressSteps(status: String) {
    val current = progressPath.indexOf(status)
    if (current < 0) return
    Ol(attrs = ProgressStepsStyle.toAttrs { attr("aria-label", "Statusschritte") }) {
        progressPath.forEachIndexed { index, step ->
            Li(
                attrs = {
                    if (index < current) attr("data-done", "")
                    if (index == current) attr("aria-current", "step")
                }
            ) {
                Span(attrs = { attr("aria-hidden", "true") }) {
                    Text(if (index < current) "✓" else "${index + 1}")
                }
                Text(invoiceStatusLabel(step))
            }
        }
    }
}

// Step 5: only the next states the server permits this actor; none for Paid and Cancelled.
@Composable
private fun nextStates(
    progress: InvoiceProgressResponse,
    busy: Boolean,
    actions: InvoiceProgressActions,
) {
    val permitted = progress.permittedTransitions
    if (permitted.isEmpty()) {
        P(attrs = HintStyle.toAttrs()) { Text(permittedLabel(permitted)) }
        return
    }
    Div(attrs = RowStyle.toAttrs()) {
        permitted.forEach { target ->
            themedButton(
                transitionLabel(target),
                { actions.select(target) },
                variant = transitionVariant(target),
                disabled = busy,
            )
        }
    }
}

// BR-087: every recorded change, oldest first, as the server ordered it.
@Composable
private fun stateHistory(history: List<InvoiceStateEventResponse>) {
    H3(attrs = LabelStyle.toAttrs()) { Text("Statusverlauf") }
    if (history.isEmpty()) {
        P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
            Text("Noch keine Statusänderung erfasst.")
        }
        return
    }
    Ol(attrs = listOf(SubListStyle, SmallStyle, StackStyle).toAttrs { id(HISTORY_ID) }) {
        history.forEach { event ->
            Li {
                Text(
                    listOfNotNull(
                            "${invoiceStatusLabel(event.previousStatus)} → " +
                                invoiceStatusLabel(event.newStatus),
                            actorLabel(event),
                            localTime(event.occurredAt),
                            event.date?.let {
                                if (event.newStatus == PAID) "Zahlungsdatum $it"
                                else "Versanddatum $it"
                            },
                        )
                        .joinToString(" · ")
                )
            }
        }
    }
}

// A8: every missing or invalid detail the server named.
@Composable
private fun readiness(problems: List<String>) {
    if (problems.isEmpty()) return
    Ul(attrs = AlertListStyle.toAttrs { id(READINESS_ID) }) {
        problems.forEach { Li { Text(it) } }
    }
}

@Composable
private fun progressProblem(problem: OnboardingProblem?) {
    if (problem == null) return
    P(
        attrs =
            AlertStyle.toAttrs {
                id(PROGRESS_ERROR_ID)
                attr("role", "alert")
                attr("tabindex", "-1")
            }
    ) {
        Text(problem.message)
    }
    LaunchedEffect(problem) {
        if (AppGlobals.isExporting) return@LaunchedEffect
        // A marked input takes focus so it can be corrected directly.
        val target = problem.fields.firstOrNull() ?: PROGRESS_ERROR_ID
        (document.getElementById(target) as? HTMLElement)?.focus()
    }
}

// After a recorded, rejected or cancelled confirmation the section title takes focus; the first
// render leaves focus with the page title. A shown problem focuses itself afterwards.
@Composable
private fun progressFocus(state: InvoiceProgressUiState) {
    val focusReturn = remember { FocusReturn() }
    val open = state.target != null
    LaunchedEffect(open) {
        if (focusReturn.pending && !open && !AppGlobals.isExporting)
            (document.getElementById(PROGRESS_TITLE_ID) as? HTMLElement)?.focus()
        focusReturn.pending = true
    }
}

private fun actorLabel(event: InvoiceStateEventResponse) =
    when {
        event.source == "SYSTEM" -> "Versand durch Jessie"
        else -> event.actorName ?: "Mitglied"
    }

private val progressPath = listOf(DRAFT, READY, SENT, PAID)

private const val PROGRESS_TITLE_ID = "invoice-progress-title"
internal const val PROGRESS_ERROR_ID = "invoice-progress-error"
private const val HISTORY_ID = "invoice-history"
private const val READINESS_ID = "invoice-readiness"
